/** Empty / error / offline copy for tech PWA `/app/status`. Mirrors admin scoreboard/webhooks/notifications. */

export type StatusEmptyCopy = {
    eyebrow: string;
    headline: string;
    body: string;
    cta: string;
    note: string;
    href?: string;
};

export const STATUS_EMPTY_SHOPS: StatusEmptyCopy = {
    eyebrow: 'STATUS · EMPTY',
    headline: 'No shops in this region yet',
    body:
        'Shop locations come from your region. This list is empty — Status does not invent a placeholder shop or a 0-job count.',
    cta: 'Retry',
    note: 'Refresh after a Glass admin adds a shop, or if Firestore just became available.',
};

export const STATUS_EMPTY_JOBS: StatusEmptyCopy = {
    eyebrow: 'JOBS · EMPTY',
    headline: 'No assigned jobs yet',
    body:
        'After invite signup you land here to set availability. Assigned work is empty — this page does not invent a 0-job metric.',
    cta: 'Open jobs',
    href: '/app/orders',
    note: 'Jobs appear on Active when a Glass admin calls assignJob. The assigned list listens — no hard refresh. Mark available and pick a location so dispatch can find you.',
};

export const STATUS_ERROR_LOAD: StatusEmptyCopy = {
    eyebrow: 'STATUS · ERROR',
    headline: 'Status could not load',
    body: 'Shop locations did not load. Status will not show a fake empty list as real work.',
    cta: 'Retry',
    note: 'Check your connection and region claims, then retry.',
};

export const STATUS_ERROR_JOBS: StatusEmptyCopy = {
    eyebrow: 'JOBS · ERROR',
    headline: 'Assigned jobs could not load',
    body: 'Your assigned jobs did not load. Status will not treat that as zero work or hide an active job.',
    cta: 'Retry',
    note: 'Check Firestore rules for your region, then retry.',
};

export const STATUS_OFFLINE: StatusEmptyCopy = {
    eyebrow: 'STATUS · OFFLINE',
    headline: 'You are offline',
    body:
        'Status cannot read shops or assigned jobs without a connection. Cached counts stay hidden instead of showing 0.',
    cta: 'Retry',
    note: 'Reconnect, then retry. Availability buttons stay so you can still mark yourself once you are back online.',
};

export type StatusSurfaceState = 'loading' | 'offline' | 'error' | 'empty' | 'ready';

export function statusSurfaceState(input: {
    loading: boolean;
    error?: string;
    offline?: boolean;
    count: number;
}): StatusSurfaceState {
    if (input.offline && input.count === 0) return 'offline';
    if (input.loading && input.count === 0) return 'loading';
    if (input.error && input.count === 0) return 'error';
    if (input.count === 0) return 'empty';
    return 'ready';
}

export function statusListCopy(surface: StatusSurfaceState): StatusEmptyCopy | null {
    if (surface === 'offline') return STATUS_OFFLINE;
    if (surface === 'error') return STATUS_ERROR_LOAD;
    if (surface === 'empty') return STATUS_EMPTY_SHOPS;
    return null;
}

export function statusFilterEmptyCopy(loadedCount: number): string {
    return `No shops match this search in the ${loadedCount} loaded.`;
}

export function statusFilterEmptyCard(loadedCount: number): StatusEmptyCopy {
    return {
        eyebrow: 'SHOPS · FILTER',
        headline: 'No matching shops',
        body: statusFilterEmptyCopy(loadedCount),
        cta: 'Clear search',
        note: 'Clear the search to see every shop loaded for your region.',
    };
}

/** Header must not treat a failed or pending jobs load as “no active job”. */
export function canUseActiveJobCopy(input: { jobsReady: boolean; activeJobCount: number }): boolean {
    return input.jobsReady && input.activeJobCount > 0;
}

export function statusAvailabilityHeadline(input: {
    available: boolean;
    jobsReady: boolean;
    activeJobCount: number;
    locationName?: string;
    hasSpecificLocation: boolean;
}): string {
    if (!input.available) return 'Are you available for work?';
    if (canUseActiveJobCopy(input)) return 'You are currently on an active job.';
    const place =
        input.hasSpecificLocation && input.locationName ? input.locationName : 'no specific location';
    return `You are currently marked as available for work at ${place}.`;
}
